import type { FormatterId } from "@blis/insights/entities/types";

/** Converts a numeric duration to milliseconds for display. */
function durationToMs(
  value: number,
  unit: NonNullable<FormatterId & { kind: "duration" }>["unit"],
): number {
  switch (unit) {
    case "ms":
      return value;
    case "s":
      return value * 1000;
    case "m":
      return value * 60_000;
    case "h":
      return value * 3_600_000;
    default:
      return value;
  }
}

/** Human-readable duration from total minutes (lab TAT style). */
function formatMinutesTotal(totalMinutes: number): string {
  const rounded = Math.round(totalMinutes);
  const days = Math.floor(rounded / (24 * 60));
  const remAfterDays = rounded - days * 24 * 60;
  const hours = Math.floor(remAfterDays / 60);
  const minutes = remAfterDays % 60;

  if (days > 0) {
    if (minutes > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${days}d ${hours}h`;
    return `${days}d`;
  }
  if (hours > 0) {
    if (minutes > 0) return `${hours}h ${minutes}m`;
    return `${hours}h`;
  }
  return `${minutes}m`;
}

/** Calendar date (YYYY-MM-DD) without local timezone shifting UTC midnight. */
export function formatExploreDateOnly(value: unknown): string {
  const s = String(value).trim();
  const isoDate = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  if (isoDate) return isoDate[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function formatExploreDuration(
  value: number,
  unit: NonNullable<FormatterId & { kind: "duration" }>["unit"],
): string {
  if (!Number.isFinite(value)) return String(value);

  const ms = durationToMs(Math.abs(value), unit);
  const sign = value < 0 ? "-" : "";

  if (ms < 1000) return `${sign}${Math.round(ms)} ms`;

  const totalMinutes = ms / 60_000;
  if (totalMinutes < 1) {
    const seconds = Math.round(ms / 1000);
    return `${sign}${seconds}s`;
  }

  return `${sign}${formatMinutesTotal(totalMinutes)}`;
}

export function formatExploreCellValue(
  value: unknown,
  formatter: FormatterId | undefined,
): string {
  if (value === null || value === undefined) return "";

  if (formatter?.kind === "percent") {
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return `${n.toFixed(formatter.decimals ?? 1)}%`;
  }

  if (formatter?.kind === "duration") {
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return formatExploreDuration(n, formatter.unit);
  }

  if (formatter?.kind === "number") {
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return n.toFixed(formatter.decimals ?? 0);
  }

  if (formatter?.kind === "date" || formatter?.kind === "datetime") {
    if (formatter.kind === "date") {
      return formatExploreDateOnly(value);
    }
    const d = new Date(String(value));
    if (Number.isNaN(d.getTime())) return String(value);
    return (
      d.toLocaleDateString("en-CA") +
      " " +
      d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    );
  }

  return String(value);
}
